import { useDay } from '@datepicker-react/hooks';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useId, useMemo, type FC, type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useNow } from 'src/hooks/use-now';
import { Colors } from 'src/styles';
import { timeZone } from 'src/utils/date-time-format';

type DstChange = 'REGULAR' | 'CET_TO_CEST' | 'CEST_TO_CET';

type DayProps = {
  dayLabel: string;
  date: Date;
  startDate: Date | null;
  endDate: Date | null;
  /**
   * Either a hovered date or dragged over date.
   */
  targetDate: Date | null;
  isDayDragging: IsDayDragging;
  onDateSelect: (date: Date) => void;
  onDayHover: (date: Date | null) => void;
  isDateBlocked: (date: Date) => boolean;
  dstChange: DstChange;
};

export const Day: FC<DayProps> = ({
  dayLabel,
  date,
  startDate,
  endDate,
  targetDate,
  isDayDragging,
  onDateSelect,
  onDayHover,
  isDateBlocked,
  dstChange,
}) => {
  const { onClick, disabledDate } = useDay({
    date,
    focusedDate: null,
    isDateFocused: () => false,
    isDateSelected: () => false,
    isDateHovered: () => false,
    isDateBlocked,
    isFirstOrLastSelectedDate: () => false,
    onDateFocus: () => {},
    onDateHover: () => {},
    onDateSelect,
  });

  const { isStartDate, isEndDate, isToday, mode } = useDayMode(
    date,
    startDate,
    endDate,
    targetDate,
    isDayDragging
  );

  const isThisDateBlocked = isDateBlocked(date);
  const isStartOrEndDate = isStartDate || isEndDate;
  const isFullRange = Boolean(startDate && endDate);
  // we don't use `disabled` of `useDraggable`, because the elements might still be interactive
  // (e.g. clickable), therefore we manually manage the draggable props
  const isDraggable = isStartOrEndDate && isFullRange && !isThisDateBlocked;

  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id: useId(),
    data: { date } satisfies DayDroppableData,
    disabled: isThisDateBlocked,
  });

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableNodeRef,
    isDragging: isThisDayDragging,
  } = useDraggable({
    id: useId(),
    data: { isStartDate } satisfies DayDraggableData,
  });

  return (
    <Tooltip content={dstTooltipMap[dstChange]}>
      {(targetProps) => (
        <DayButton
          ref={(node) => {
            setDraggableNodeRef(node);
            setDroppableNodeRef(node);
          }}
          type="button"
          data-testid={`day-${date.toISOString()}`}
          onClick={onClick}
          onMouseEnter={() => onDayHover(date)}
          onMouseLeave={() => onDayHover(null)}
          disabled={disabledDate}
          isDraggable={isDraggable}
          isDayDragging={isDayDragging}
          {...targetProps}
          {...(isDraggable && attributes)}
          {...(isDraggable && listeners)}
        >
          <DayButtonInner
            mode={mode}
            isToday={isToday}
            isThisDayDragging={isThisDayDragging}
            isDisabled={disabledDate}
          >
            {dayLabel}
            {dstAppendixMap[dstChange] ??
              (isToday ? <DayAppendix aria-hidden>●</DayAppendix> : null)}
          </DayButtonInner>
        </DayButton>
      )}
    </Tooltip>
  );
};

type DayButtonProps = {
  isDraggable: boolean;
  isDayDragging: IsDayDragging;
};

const DayButton = styled.button<DayButtonProps>`
  display: grid;
  background: none;
  border: none;
  padding: 0;
  width: 100%; /* for CEST/CET label */
  height: 3rem;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}

  ${({ isDraggable }) =>
    isDraggable &&
    css`
      cursor: grab;
    `}

  ${({ isDayDragging, disabled }) =>
    !isDayDragging &&
    !disabled &&
    css`
      &:hover [mode='default'] {
        border-radius: 2rem;
        background: ${Colors.brandLight4};
      }
    `}
`;

type DayMode =
  | 'default'
  | 'target-single'
  | 'target-start'
  | 'target-end'
  | 'target-inner'
  | 'single'
  | 'start'
  | 'end'
  | 'inner'
  | 'unselect';

const dayModeStyles: Record<DayMode, ReturnType<typeof css>> = {
  default: css``,
  'target-single': css`
    border: 2px dashed ${Colors.brandLight4};
    background-color: ${Colors.brandLight5};
    border-radius: 2rem;
  `,
  'target-start': css`
    border: 2px dashed ${Colors.brandLight4};
    background-color: ${Colors.brandLight5};
    border-radius: 2rem 0 0 2rem;
  `,
  'target-end': css`
    border: 2px dashed ${Colors.brandLight4};
    background-color: ${Colors.brandLight5};
    border-radius: 0 2rem 2rem 0;
  `,
  'target-inner': css`
    border: 2px dashed ${Colors.brandLight4};
    background-color: ${Colors.brandLight5};
  `,
  single: css`
    background-color: ${Colors.brandSecondary};
    color: #fff;
    border-radius: 2rem;
  `,
  start: css`
    background-color: ${Colors.brandSecondary};
    color: #fff;
    border-radius: 2rem 0 0 2rem;
  `,
  end: css`
    background-color: ${Colors.brandSecondary};
    color: #fff;
    border-radius: 0 2rem 2rem 0;
  `,
  inner: css`
    background-color: ${Colors.brandLight4};
  `,
  unselect: css`
    background-color: ${Colors.brandLight5};
  `,
};

type DayButtonInnerProps = {
  mode: DayMode;
  isToday: boolean;
  isThisDayDragging: boolean;
  isDisabled: boolean;
};

const DayButtonInner = styled.div<DayButtonInnerProps>`
  position: relative; /* for <DayAppendix /> */
  align-content: center;
  color: ${Colors.brand};
  border: 2px dashed transparent;

  ${({ isToday }) =>
    isToday &&
    css`
      color: ${Colors.brandSecondary};
    `}

  ${({ mode }) => dayModeStyles[mode]}

  ${({ isThisDayDragging }) =>
    isThisDayDragging &&
    css`
      border: 2px dashed ${Colors.brandLight4};
    `}

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      color: ${Colors.inactive};
    `}

  ${({ isDisabled, mode }) =>
    isDisabled &&
    (mode === 'single' || mode === 'start' || mode === 'end') &&
    css`
      color: white;
      background-color: ${Colors.inactive};
    `}
`;

const DayAppendix = styled.span`
  position: absolute;
  left: 0;
  width: 100%;
  top: 18.5px;
  font-size: 0.8rem;
`;

const dstAppendixMap: Record<DstChange, ReactNode> = {
  REGULAR: null,
  CET_TO_CEST: <DayAppendix>CEST</DayAppendix>,
  CEST_TO_CET: <DayAppendix>CET</DayAppendix>,
};

const dstTooltipMap: Record<DstChange, ReactNode> = {
  REGULAR: null,
  CET_TO_CEST: 'Start of CEST, clocks move forward 1 hour.',
  CEST_TO_CET: 'Start of CET, clocks move back 1 hour.',
};

export type IsDayDragging = 'start' | 'end' | 'single' | null;

export type DayDroppableData = {
  date: Date;
};

export type DayDraggableData = {
  isStartDate: boolean;
};

function useDayMode(
  currentDate: Date,
  startDate: Date | null,
  endDate: Date | null,
  targetDate: Date | null,
  isDayDragging: IsDayDragging
) {
  const now = useNow();

  return useMemo(() => {
    const nowBerlin = toZonedTime(now, timeZone);
    const currentDateBerlin = toZonedTime(currentDate, timeZone);
    const startDateBerlin = startDate && toZonedTime(startDate, timeZone);
    const endDateBerlin = endDate && toZonedTime(endDate, timeZone);
    const targetDateBerlin = targetDate && toZonedTime(targetDate, timeZone);

    const isToday = isSameDay(currentDateBerlin, nowBerlin);
    const isStartDate = startDateBerlin
      ? isSameDay(startDateBerlin, currentDateBerlin)
      : false;
    const isEndDate = endDateBerlin
      ? isSameDay(endDateBerlin, currentDateBerlin)
      : false;

    let mode: DayMode = 'default';

    if (
      startDateBerlin &&
      endDateBerlin &&
      isAfterDay(currentDateBerlin, startDateBerlin) &&
      isBeforeDay(currentDateBerlin, endDateBerlin)
    ) {
      mode = 'inner';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      endDateBerlin &&
      ((isDayDragging === 'start' &&
        isAfterDay(currentDateBerlin, startDateBerlin) &&
        isBeforeDay(currentDateBerlin, targetDateBerlin)) ||
        (isDayDragging === 'end' &&
          isAfterDay(currentDateBerlin, targetDateBerlin) &&
          isBeforeDay(currentDateBerlin, endDateBerlin)))
    ) {
      mode = 'unselect';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      endDateBerlin &&
      isSameDay(currentDateBerlin, targetDateBerlin) &&
      ((isDayDragging === 'start' &&
        isBefore(targetDateBerlin, endDateBerlin)) ||
        (isDayDragging === 'end' &&
          isBefore(targetDateBerlin, startDateBerlin)))
    ) {
      // dragging over case
      mode = 'target-start';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      !endDateBerlin &&
      isBeforeDay(currentDateBerlin, startDateBerlin) &&
      isSameDay(currentDateBerlin, targetDateBerlin)
    ) {
      // hover preview case
      mode = 'target-start';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      endDateBerlin &&
      isSameDay(currentDateBerlin, targetDateBerlin) &&
      ((isDayDragging === 'start' &&
        isBefore(endDateBerlin, targetDateBerlin)) ||
        (isDayDragging === 'end' &&
          isBefore(startDateBerlin, targetDateBerlin)))
    ) {
      // dragging over case
      mode = 'target-end';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      !endDateBerlin &&
      isAfterDay(currentDateBerlin, startDateBerlin) &&
      isSameDay(currentDateBerlin, targetDateBerlin)
    ) {
      // hover preview case
      mode = 'target-end';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      endDateBerlin &&
      ((isDayDragging === 'start' &&
        isBeforeDay(currentDateBerlin, startDateBerlin) &&
        isAfterDay(currentDateBerlin, targetDateBerlin)) ||
        (isDayDragging === 'start' &&
          isBeforeDay(currentDateBerlin, targetDateBerlin) &&
          isAfterDay(currentDateBerlin, endDateBerlin)) ||
        (isDayDragging === 'end' &&
          isAfterDay(currentDateBerlin, endDateBerlin) &&
          isBeforeDay(currentDateBerlin, targetDateBerlin)) ||
        (isDayDragging === 'end' &&
          isAfterDay(currentDateBerlin, targetDateBerlin) &&
          isBeforeDay(currentDateBerlin, startDateBerlin)))
    ) {
      // dragging over case
      mode = 'target-inner';
    }

    if (
      targetDateBerlin &&
      startDateBerlin &&
      !endDateBerlin &&
      ((isBeforeDay(currentDateBerlin, startDateBerlin) &&
        isAfterDay(currentDateBerlin, targetDateBerlin)) ||
        (isAfterDay(currentDateBerlin, startDateBerlin) &&
          isBeforeDay(currentDateBerlin, targetDateBerlin)))
    ) {
      // hover preview case
      mode = 'target-inner';
    }

    if (
      targetDateBerlin &&
      isDayDragging === 'single' &&
      isSameDay(currentDateBerlin, targetDateBerlin)
    ) {
      mode = 'target-single';
    }

    if (isStartDate) {
      mode = 'start';
      if (!endDateBerlin) {
        if (targetDateBerlin) {
          if (isBeforeDay(targetDateBerlin, startDateBerlin!)) {
            mode = 'end';
          } else if (isAfterDay(targetDateBerlin, startDateBerlin!)) {
            mode = 'start';
          } else {
            mode = 'single';
          }
        } else mode = 'single';
      }
    }

    if (isEndDate) {
      mode = 'end';
      if (isStartDate) mode = 'single';
    }

    return { isToday, isStartDate, isEndDate, mode };
  }, [currentDate, startDate, endDate, targetDate, isDayDragging, now]);
}

// Ignore time for the isBefore comparison.
function isBeforeDay(date: Date, dateToCompare: Date) {
  const immutableDate = new Date(date.getTime());
  const immutableDateToCompare = new Date(dateToCompare.getTime());

  immutableDate.setHours(0, 0, 0, 0);
  immutableDateToCompare.setHours(0, 0, 0, 0);

  return isBefore(immutableDate, immutableDateToCompare);
}

// Ignore time for the isAfter comparison.
function isAfterDay(date: Date, dateToCompare: Date) {
  const immutableDate = new Date(date.getTime());
  const immutableDateToCompare = new Date(dateToCompare.getTime());

  immutableDate.setHours(0, 0, 0, 0);
  immutableDateToCompare.setHours(0, 0, 0, 0);

  return isAfter(immutableDate, immutableDateToCompare);
}
